import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { pickBy, identity, isEmpty } from 'lodash';
import clsx from 'clsx';
import { asyncActions, availabilitiesActions } from '@docavenue/core';
import {
  CardHcd,
  Availability,
  AvailabilityClosestWrapper,
} from '@maiia/model/generated/model/api-patient/api-patient';
import { useRouter } from 'next/router';
import { createSelector } from 'reselect';
import { Button, IconButton } from '@mui/material';

import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
} from '@maiia/design-system';

import Text from '../../atoms/Text/Text';
import AvailabilityDay from './AvailabilityDay';
import { closestAvailabilitiesActions } from '../../../src/actions';
import AvailabilityCalendarSkeleton from './AvailabilityCalendarSkeleton';
import {
  transform,
  getPreviousPeriod,
  getNextPeriod,
  getPeriod,
} from './functions';
import ClosestAvailability from './ClosestAvailability';
import { dayjsWithTimezone } from '@/src/utils';
import { CENTER, CONSULTATION_REASON, DEFAULT_TIMEZONE } from '@/src/constants';
import { useAsyncError } from '@/src/hooks';
import FirstAvailability from '../FirstAvailability/FirstAvailability';
import { useTranslation } from '@/src/i18n';
import { nearestAvailability } from '../../utils/availabilities';
import { isNoPractitionerSelected } from '../../utils/card';
import useIsPrescriber from '@/src/hooks/useIsPrescriber';
import { analyticsEvent } from '@/src/analytic_tag_manager';
import { suggestionItemSelector } from '@/src/selectors';

type Props = {
  isFirstAvailabilities?: boolean;
  card?: CardHcd;
  practitionerId?: string;
  centerId?: string;
  consultationReasonId?: string;
  consultationReasonName?: string;
  consultationType?: string;
  settings: any;
  onClick: (timeSlot: Availability) => void;
  innerRef: any;
  onSuccess?: ({ total }: { total: number | null }) => void;
  onLoad?: () => void;
  specialityId?: string;

  // Specifial props for appointment series
  multiple?: boolean;
  onChange?: (slots: string[]) => void;
  highlighSelectItem?: boolean;
};

const defaultViewableSlots = 3;
const noop = () => {};
const selectPractitionersIds = createSelector<any, CardHcd[], string[]>(
  [state => state?.practitioners?.items ?? []],
  items => items.map(x => x.professional?.practitionerId!),
);

const AvailabilityCalendar = ({
  isFirstAvailabilities,
  card,
  practitionerId,
  centerId,
  consultationReasonId,
  consultationReasonName,
  consultationType,
  settings,
  onClick,
  innerRef,
  onLoad = noop,
  onSuccess = noop,
  specialityId,
  multiple,
  onChange,
  highlighSelectItem,
}: Props) => {
  const dispatch = useDispatch();
  const isPrescriber = useIsPrescriber();
  const throwError = useAsyncError();
  const timezone = settings?.timeZone || DEFAULT_TIMEZONE;
  const dayjsTZ = dayjsWithTimezone(timezone);
  const router = useRouter();
  const { t } = useTranslation();
  const suggestion = useSelector(suggestionItemSelector);
  const [isLoading, setIsLoading] = React.useState(false);

  const [selectedItems, setSelectedItems] = React.useState<any[]>([]);

  const [viewableSlots, setViewableSlots] = React.useState(
    defaultViewableSlots,
  );
  const today = router.query.focus
    ? dayjsTZ(router.query.focus as string).subtract(2, 'days')
    : dayjsTZ();
  const [from, setFrom] = React.useState(today);
  const [closestAvailabilities, setClosestAvailabilities] = React.useState<any>(
    null,
  );
  const [closestsTitle, setClosestTitle] = React.useState<string>();
  const [to, setTo] = React.useState(
    dayjsTZ()
      .add(4, 'day')
      .endOf('day'),
  );
  const [availabilities, setAvailabilities] = React.useState<Availability[]>(
    [],
  );
  const consultationReason = useSelector(
    (state: any) => state?.consultationReasonsByName?.item,
  );

  const practitionersIds = useSelector(selectPractitionersIds);

  const onTimeSlotClick = React.useCallback(
    slot => {
      if (highlighSelectItem)
        setSelectedItems(items => {
          const isSelected = items.some(s => s.id === slot.id);
          let ret: any[] = [];
          if (multiple) {
            ret = isSelected
              ? [...items.filter(s => s.id !== slot.id)]
              : [...items, slot];
          } else {
            ret = isSelected ? [] : [slot];
          }
          if (onChange) onChange(ret);
          return ret;
        });

      if (!isNoPractitionerSelected(practitionerId)) {
        return onClick(slot);
      }
      const slots = availabilities.filter(
        x => x.startDateTime === slot.startDateTime,
      );
      if (slots.length === 1) {
        return onClick(slot);
      }
      const randomSlot = slots[Math.floor(Math.random() * slots.length)];
      return onClick(randomSlot);
    },
    [onClick, availabilities, practitionerId],
  );

  const showFirstAvailabilities = Boolean(!consultationType);

  React.useEffect(() => {
    if (
      (consultationReasonName &&
        consultationReasonName !== consultationReason?.name) ||
      !consultationType // Not go to last step
    ) {
      return;
    }
    let cancelled = false;
    const loadData = async () => {
      // 13/10/22, @nampnq change .add(1, 'minute').startOf('minute') => .add(1, 'minute').set('second', 0).set('ms', 0) because dayjs handle incorrect
      // >> f=dayjsTZ('2022-10-18T00:00:00.000Z','America/Scoresbysund')
      // >> f.clone().add(1, 'minute').startOf('minute').toISOString()
      // '2022-10-17T22:01:00.000Z'
      setIsLoading(true);
      onLoad();
      const defaultParams = pickBy(
        {
          centerId,
          practitionerId,
          consultationReasonId,
          from: from
            .clone()
            .add(1, 'minute')
            .set('second', 0)
            .set('ms', 0)
            .toISOString(),
          to: to.toISOString(),
          limit: 2016, // 12 slots per hour * 24 hours * 7 days
        },
        identity,
      );
      const noPratPrefer = {
        centerId,
        consultationReasonName,
        consultationReasonType: consultationType,
        from: from
          .clone()
          .add(1, 'minute')
          .set('second', 0)
          .set('ms', 0)
          .toISOString(),
        to: to.toISOString(),
        limit: 2016, // 12 slots per hour * 24 hours * 7 days
        specialityId,
      };
      const noPractitionerSelected = isNoPractitionerSelected(practitionerId);
      const specialityFilter = card?.type === CENTER ? { specialityId } : {};
      const expertiseFilter =
        // eslint-disable-next-line no-nested-ternary
        suggestion.objectType === 'expertise'
          ? suggestion.type?.name === CONSULTATION_REASON
            ? { consultationReasonLabel: suggestion.name }
            : { expertiseId: suggestion.id }
          : {};
      const params = {
        ...(noPractitionerSelected ? noPratPrefer : defaultParams),
        ...specialityFilter,
        ...expertiseFilter,
      };
      const data = (await asyncActions(
        dispatch,
        availabilitiesActions.getActions(isPrescriber).getList(params),
      )) as { items: Availability[] };

      if (cancelled) return;
      if (process.env.NODE_ENV !== 'production')
        // eslint-disable-next-line no-console
        console.log(
          'practitionersIds from availabilities',
          new Set(data.items.map(x => x.practitionerId)),
          'practitionersIds',
          practitionersIds,
          'intersection',
          (new Set(data.items.map(x => x.practitionerId)) as any)?.intersection(
            new Set(practitionersIds),
          ),
        );
      setAvailabilities(
        noPractitionerSelected
          ? data.items.filter(x => practitionersIds.includes(x.practitionerId))
          : data.items,
      );
      if (isEmpty(data.items)) {
        const closestParams = {
          centerId,
          ...specialityFilter,
          ...(noPractitionerSelected ? {} : { practitionerId }),
          consultationReasonId: noPractitionerSelected
            ? undefined
            : consultationReasonId,
          consultationReasonName: noPractitionerSelected
            ? consultationReasonName
            : undefined,
          consultationReasonType: noPractitionerSelected
            ? consultationType
            : undefined,
          from: from
            .clone()
            .add(1, 'minute')
            .set('second', 0)
            .set('ms', 0)
            .toISOString(),
        };
        let closests = (await asyncActions(
          dispatch,
          closestAvailabilitiesActions
            .getActions(isPrescriber)
            .getList(closestParams),
        )) as AvailabilityClosestWrapper;
        if (!closests?.availabilityCount) {
          closests = (await asyncActions(
            dispatch,
            closestAvailabilitiesActions.getActions(isPrescriber).getList({
              ...closestParams,
              from: today
                .clone()
                .add(1, 'minute')
                .set('second', 0)
                .set('ms', 0)
                .toISOString(),
            }),
          )) as AvailabilityClosestWrapper;
        }
        if (cancelled) return;
        const closestDate = nearestAvailability(
          closests,
          consultationType,
          timezone,
        );
        if (closestDate) {
          setClosestTitle(
            from.isAfter(closestDate)
              ? 'availiable_in_pass_first_availiable_is'
              : undefined,
          );
        }
        setClosestAvailabilities(closests);
      } else {
        setClosestAvailabilities(null);
      }
      setIsLoading(false);
      onSuccess({ total: data.items?.length });
    };
    onSuccess({ total: null }); // Data is not ready
    loadData().catch(throwError);
    return () => {
      cancelled = true;
      setIsLoading(false);
    };
  }, [
    from.format('DD-MM-YYYY'),
    to.format('DD-MM-YYYY'),
    centerId,
    practitionerId,
    consultationReasonId,
    consultationReasonName,
    consultationType,
    consultationReason,
    specialityId,
    practitionersIds,
  ]);

  const transformedAvailabilities = React.useMemo(
    () => transform(from, to, availabilities),
    [from, to, availabilities],
  );

  const substitutePractitioner = React.useMemo(() => {
    for (const item of transformedAvailabilities) {
      const found = item.slots.find(s => s?.substitute?.userId);
      if (found) return found.substitute;
    }
  }, [transformedAvailabilities]);

  const maxAvailabilities = React.useMemo(
    () =>
      Math.max(...transformedAvailabilities.map(({ slots }) => slots.length)),
    [transformedAvailabilities],
  );

  const isExpandable = React.useMemo(
    () => maxAvailabilities > Math.max(defaultViewableSlots, viewableSlots),
    [maxAvailabilities, defaultViewableSlots, viewableSlots],
  );

  const isBackable = React.useMemo(() => {
    const [, newTo] = getPreviousPeriod(from);
    return newTo > dayjsTZ();
  }, [from]);

  const expand = () => {
    analyticsEvent({
      category: 'Booking',
      action: 'ClickMoreAvailabilities',
    });
    setViewableSlots(maxAvailabilities || defaultViewableSlots);
  };

  React.useImperativeHandle(innerRef, () => ({
    backToCurrentDate: (focus?: string) => {
      const [newFrom, newTo] = getPeriod(
        focus ? dayjsTZ(focus).subtract(2, 'days') : dayjsTZ(),
      );
      setFrom(newFrom);
      setTo(newTo);
    },
  }));

  const handleShowAvailabilitiesCalendar = React.useCallback(
    date => {
      if (
        (maxAvailabilities === 0 || !consultationType) &&
        isFirstAvailabilities
      ) {
        const [newFrom, newTo] = getPeriod(dayjsTZ(date));
        setFrom(newFrom);
        setTo(newTo);
      }
    },
    [maxAvailabilities, consultationType],
  );

  if (isLoading) return <AvailabilityCalendarSkeleton />;

  if (isFirstAvailabilities && showFirstAvailabilities)
    return (
      <FirstAvailability
        card={card}
        consultationType={consultationType}
        centerId={centerId}
        practitionerId={practitionerId}
        consultationReasonName={consultationReasonName}
        closestAvailabilities={closestAvailabilities}
        onClick={date => {
          handleShowAvailabilitiesCalendar(date);
        }}
        specialityId={specialityId}
      />
    );

  return (
    <div className="search-card__rdv availability-calendar percy-hidden">
      <IconButton
        sx={{ px: 0, '&:hover': { backgroundColor: 'transparent' } }}
        data-cy="btn-calendar-previous"
        className={clsx('calendar__navigation', {
          disabled: !isBackable,
        })}
        onClick={() => {
          if (!isBackable) return;
          const [newFrom, newTo] = getPreviousPeriod(from);
          setFrom(newFrom);
          setTo(newTo);
          setViewableSlots(defaultViewableSlots);
          analyticsEvent({
            category: 'Booking',
            action: 'ClickPrevWeek',
          });
        }}
      >
        <IconChevronLeft />
      </IconButton>
      {availabilities.length === 0 && (
        <ClosestAvailability
          consultationType={consultationType}
          closestAvailabilities={closestAvailabilities}
          timezone={timezone}
          navigate={date => {
            const [newFrom, newTo] = getPeriod(date);
            setFrom(newFrom);
            setTo(newTo);
          }}
          title={closestsTitle}
        />
      )}
      {transformedAvailabilities.map(
        ({ date, slots, timezone: availabilityTimeZone }) => (
          <AvailabilityDay
            key={`${date.format('DD-MM-YYYY')}_${centerId}_${practitionerId}`}
            childPrefixKey={`${date.format(
              'DD-MM-YYYY',
            )}_${centerId}_${practitionerId}`}
            date={date}
            slots={slots}
            viewableSlots={viewableSlots}
            onClick={onTimeSlotClick}
            timezone={availabilityTimeZone}
            selectedItems={selectedItems}
          />
        ),
      )}
      <IconButton
        sx={{ px: 0, '&:hover': { backgroundColor: 'transparent' } }}
        data-cy="btn-calendar-next"
        className="calendar__navigation"
        onClick={() => {
          const [newFrom, newTo] = getNextPeriod(to);
          setFrom(newFrom);
          setTo(newTo);
          setViewableSlots(defaultViewableSlots);
          analyticsEvent({
            category: 'Booking',
            action: 'ClickNextWeek',
          });
        }}
      >
        <IconChevronRight aria-label={t('see_the_next_few_days')} />
      </IconButton>
      {substitutePractitioner && (
        <Text
          variant="paragraph"
          component="p"
          size="small"
          className="has-sub"
        >
          {t('rdv-insured-by')}
          {`${substitutePractitioner?.firstName} ${substitutePractitioner?.lastName}`}
        </Text>
      )}
      {isExpandable && (
        <Button
          sx={theme => ({
            color: theme.palette.text.primary,
            // custom values to match previous design
            fontSize: 12,
            fontWeight: 400,
            flexBasis: '100%',

            // Override MUI's CSS selector
            '& *:nth-of-type(1)': {
              fontSize: 16,
            },
          })}
          endIcon={<IconChevronDown aria-hidden="true" />}
          onClick={expand}
          data-cy="btn-calendar-show-more"
        >
          {t('more-availabilities')}
        </Button>
      )}
    </div>
  );
};

export default React.forwardRef<any, Omit<Props, 'innerRef'>>((props, ref) => (
  <AvailabilityCalendar innerRef={ref} {...props} />
));
